import { useCallback, useRef, useState } from 'react';

export interface LeavingPanel<T> {
  /** Render the panel while true. Stays true through the exit animation. */
  mounted: boolean;
  /** Pass to DockedPanel/CompanionPanel `shown`. False starts the exit. */
  shown: boolean;
  /** Pass to DockedPanel/CompanionPanel `onExited`. Unmounts the panel. */
  onExited: () => void;
  /** The content from the last render where `open` was true. Render this
   *  (not the live value) so the panel doesn't go blank while it slides out. */
  lastContent: T | undefined;
}

/**
 * Keeps a docked panel mounted while its exit animation plays.
 *
 *   const leave = useLeavingPanel(selected != null, selected);
 *   {leave.mounted && (
 *     <DockedPanel shown={leave.shown} onExited={leave.onExited}>
 *       <Detail item={leave.lastContent!} />
 *     </DockedPanel>
 *   )}
 *
 * `instant` is for a panel that another panel on the same side replaces:
 * pass true and the panel unmounts at once, as it did before this hook, so
 * the newcomer takes the slot without two panels sliding past each other.
 * It is read on the render where `open` goes false.
 *
 * The panel must never unmount mid-animation (the "animated fade freeze"
 * lesson), so `mounted` only drops after `onExited` fires. If `open` flips
 * back to true during the exit, the panel slides back in and stays.
 */
export function useLeavingPanel<T>(
  open: boolean,
  content?: T,
  options?: { instant?: boolean }
): LeavingPanel<T> {
  const [prevOpen, setPrevOpen] = useState(open);
  const [leaving, setLeaving] = useState(false);
  // A ref, not state: callers often pass a fresh object each render, which
  // would loop a set-state-in-render.
  const lastContent = useRef<T | undefined>(content);
  if (open) lastContent.current = content;

  // Derive state during render (React's documented pattern for state that
  // follows a prop) so there is no frame where `open` is false but the
  // panel has already unmounted.
  if (open !== prevOpen) {
    setPrevOpen(open);
    setLeaving(!open && !options?.instant);
  }

  const onExited = useCallback(() => setLeaving(false), []);

  return {
    mounted: open || leaving,
    shown: open,
    onExited,
    lastContent: lastContent.current,
  };
}
